import type { ReactNode } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { webColors } from '@liminal/brand/semantic';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Link } from '@liminal/web-ui/primitives/Link';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { StatGrid } from '@liminal/web-ui/display/StatGrid';
import { DetailRow } from '@liminal/web-ui/display/DetailRow';
import { CopyableId } from '@liminal/web-ui/display/CopyableId';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import { Skeleton } from '@liminal/web-ui/feedback/Skeleton';
import { Icon } from '@liminal/web-ui/icons/Icon';
import { formatOrderDate } from '@liminal/utils/date';
import { RewardBudget_State, RewardMerchant_State, RewardPayment_State } from '@/api/generated';
import { getErrorMessage } from '@/errors/messages';
import { useRewardMerchants, useRewardPayments, useRewardProgramme } from '@/hooks/useRewards';
import { budgetState } from '@/components/ui/statusHelpers';
import { durationSeconds, durationText, groupDecimal, isPositive, parseResourceId, usdText } from '@/lib/rewards';
import { BudgetUsage } from '../components/BudgetUsage';

/** Partners are a handful; one page answers whether any is live. */
const MERCHANTS_PAGE = 200;
/** A review queue longer than this is shown as "200+". */
const REVIEW_COUNT_PAGE = 200;

interface SetupStep {
  readonly id: string;
  readonly done: boolean;
  readonly title: string;
  readonly detail: string;
  readonly action: ReactNode;
}

function SetupChecklist({ steps }: { readonly steps: readonly SetupStep[] }) {
  return (
    <Card variant="elevated">
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="h6">Set up Partner Rewards</Typography>
          <Typography variant="body2" color="text.secondary">
            Customers who pay a partner by Pix choose: earn LIL, or pay with LIL and get the money back. Three things have
            to be in place first.{' '}
            <Link component={RouterLink} to="/rewards/how-it-works">
              How it works
            </Link>
          </Typography>
        </Stack>
        {steps.map((step, index) => (
          <Stack
            key={step.id}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ pt: 2, borderTop: `1px solid ${webColors.border.light}` }}
          >
            <Box sx={{ width: 28, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
              {step.done ? (
                <Icon name="checkmark-circle" size={24} color={webColors.state.success} title="Done" />
              ) : (
                <Typography variant="subtitle1" color="text.secondary">
                  {index + 1}
                </Typography>
              )}
            </Box>
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={step.done ? { color: webColors.text.tertiary, textDecoration: 'line-through' } : undefined}
              >
                {step.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {step.detail}
              </Typography>
            </Stack>
            {!step.done && <Box sx={{ flexShrink: 0 }}>{step.action}</Box>}
          </Stack>
        ))}
      </Stack>
    </Card>
  );
}

/** The operator's first look at Partner Rewards: what needs a person, what money is there, and what each budget has left. */
export function OverviewPage() {
  const navigate = useNavigate();
  const programme = useRewardProgramme();
  const merchants = useRewardMerchants(MERCHANTS_PAGE, '');
  const review = useRewardPayments(`state = ${RewardPayment_State.STATE_REVIEW}`, REVIEW_COUNT_PAGE, '');
  const p = programme.data;
  const owedWindow = durationSeconds(p?.owedAlertDuration);
  const budgets = p?.budgets ?? [];
  const liveBudgets = budgets.filter((b) => b.state !== RewardBudget_State.STATE_CLOSED);

  const reviewRows = review.data?.rewardPayments?.length ?? 0;
  const reviewCount = `${reviewRows}${review.data?.nextPageToken ? '+' : ''}`;
  const owedCount = p?.owedTooLongCount ?? 0;

  const openReview = () => navigate(`/rewards/payments?${new URLSearchParams({ state: RewardPayment_State.STATE_REVIEW })}`);
  const openOwed = () => {
    if (owedWindow === null) return;
    const owedBefore = new Date(Date.now() - owedWindow * 1000).toISOString();
    navigate(`/rewards/payments?${new URLSearchParams({ state: RewardPayment_State.STATE_PAYING, owedBefore })}`);
  };

  const setupLoaded = programme.isSuccess && merchants.isSuccess;
  const hasBudget = liveBudgets.length > 0;
  const steps: SetupStep[] = [
    {
      id: 'fund',
      done: isPositive(p?.treasuryUsdc),
      title: 'Fund the treasury',
      detail: 'Send USDC on Base to the treasury Safe. Pay with LIL pays customers back from it.',
      action: p?.treasurySafeAddress ? <CopyableId value={p.treasurySafeAddress} /> : null,
    },
    {
      id: 'budget',
      done: hasBudget,
      title: 'Add a budget',
      detail: 'Caps how much Pay with LIL may pay back, in total and per payment.',
      action: (
        <Button size="sm" startIcon="plus" onClick={() => navigate('/rewards/budgets/new')}>
          New budget
        </Button>
      ),
    },
    {
      id: 'partner',
      done: (merchants.data?.rewardMerchants ?? []).some((m) => m.state === RewardMerchant_State.STATE_ACTIVE),
      title: 'Add a partner',
      detail: hasBudget
        ? 'A merchant whose Pix payments earn LIL or can be paid with LIL.'
        : 'A merchant whose Pix payments earn LIL or can be paid with LIL. Add a budget first.',
      action: (
        <Button size="sm" startIcon="plus" disabled={!hasBudget} onClick={() => navigate('/rewards/partners/new')}>
          New partner
        </Button>
      ),
    },
  ];

  return (
    <Stack spacing={3}>
      {setupLoaded && steps.some((s) => !s.done) && <SetupChecklist steps={steps} />}

      {programme.isError ? (
        <WarningCard tone="error">{`The treasury figures could not be read: ${getErrorMessage(programme.error)}`}</WarningCard>
      ) : (
        <StatGrid
          columns={3}
          stats={[
            {
              id: 'free',
              label: 'Free to pay back',
              value: usdText(p?.headroomUsd),
              tone: p?.lowFunds ? 'error' : undefined,
              hint: p?.lowFunds ? 'Low: top up the treasury' : 'Treasury USDC less what is owed',
              loading: programme.isLoading,
            },
            {
              id: 'review',
              label: 'Needs review',
              value: reviewCount,
              tone: reviewRows > 0 ? 'error' : 'neutral',
              hint: 'Refunded after the customer chose',
              loading: review.isLoading,
              onClick: openReview,
            },
            {
              id: 'owed',
              label: 'Money owed too long',
              value: String(owedCount),
              tone: owedCount > 0 ? 'error' : 'neutral',
              hint: owedWindow === null ? 'LIL taken, money not sent' : `LIL taken, money not sent within ${durationText(owedWindow)}`,
              loading: programme.isLoading,
              onClick: owedWindow === null ? undefined : openOwed,
            },
          ]}
        />
      )}

      {review.isError && (
        <WarningCard tone="error">{`The review queue could not be read: ${getErrorMessage(review.error)}`}</WarningCard>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, alignItems: 'start' }}>
        <Card variant="elevated">
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="h6">Treasury</Typography>
              {p?.lowFunds && <StatusPill tone="error" label="Low funds" size="sm" />}
            </Stack>
            {programme.isLoading ? (
              <Skeleton height={120} />
            ) : (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <DetailRow layout="stacked" label="USDC held" value={usdText(p?.treasuryUsdc)} />
                  <DetailRow layout="stacked" label="Owed to customers" value={usdText(p?.owedUsd)} />
                  <DetailRow layout="stacked" label="LIL collected" value={p?.treasuryLil ? groupDecimal(p.treasuryLil) : '—'} />
                  <DetailRow
                    layout="stacked"
                    label="Low-funds alert"
                    value={
                      p?.lowFundsThresholdUsd === undefined
                        ? '—'
                        : isPositive(p.lowFundsThresholdUsd)
                          ? `Below ${usdText(p.lowFundsThresholdUsd)} free`
                          : 'Off'
                    }
                  />
                </Box>
                <DetailRow
                  layout="stacked"
                  label="Safe address (Base)"
                  value={p?.treasurySafeAddress ? <CopyableId value={p.treasurySafeAddress} /> : '—'}
                />
                <Typography variant="caption" color="text.secondary">
                  {p?.balanceReadTime ? `Balances read ${formatOrderDate(p.balanceReadTime)}` : 'Balances not read yet'}
                </Typography>
              </>
            )}
          </Stack>
        </Card>

        <Card variant="elevated">
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Budgets</Typography>
              <Button variant="link" size="sm" onClick={() => navigate('/rewards/budgets')}>
                See all
              </Button>
            </Stack>
            {programme.isLoading ? (
              <Skeleton height={120} />
            ) : liveBudgets.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No open budgets. A budget caps how much Pay with LIL may pay back.
              </Typography>
            ) : (
              liveBudgets.map((b) => {
                const id = b.budget ? parseResourceId(b.budget, 'rewardBudgets') : null;
                const state = budgetState(b.state);
                return (
                  <Stack key={b.budget} spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle2">
                        {id === null ? (
                          (b.displayName ?? b.budget)
                        ) : (
                          <Link component={RouterLink} to={`/rewards/budgets/${id}`}>
                            {b.displayName ?? id}
                          </Link>
                        )}
                      </Typography>
                      {b.state !== RewardBudget_State.STATE_OPEN && <StatusPill tone={state.tone} label={state.label} size="sm" />}
                    </Stack>
                    <BudgetUsage limitUsd={b.limitUsd} spentUsd={b.spentUsd} reservedUsd={b.reservedUsd} />
                  </Stack>
                );
              })
            )}
          </Stack>
        </Card>
      </Box>
    </Stack>
  );
}
